// @givmo/board — a committee's decisions, suggested for the board's next meeting (CBG-YR6-CONFIRM,
// frame c7; the backend's contract `board-committee-confirmation.md` §4–§6).
//
// BoardSource's practice (LP14: "decisions made by the executive committee should be confirmed by the
// full board at the following board meeting"), held to plan §3.6: a SUGGESTION the officer adds or puts
// away, never an item Board places and never a rule — a committee's decision binds as recorded. In
// CBG-YR3-ANNUAL's shape: computed on read by the backend, on the board's own meeting, for someone who
// can change its agenda (`board_meeting:write`), only for a committee whose practice is on, and only
// while it has decisions recorded under that practice that are on no board meeting's agenda. A closed
// session's decisions are never listed.
//
//   * "Add to the agenda" (primary): the backend recomputes under the meeting's lock and appends one
//     ordinary item per committee, its decisions listed in its description; the card then has nothing
//     left to suggest, and goes.
//   * "Not for this meeting" (quiet): recorded on the meeting, reversibly — one quiet line, "Show the
//     committee decisions", brings the card back. The next board meeting still suggests them.
// Its words are `committee-confirmation-words.ts`' (approved in CBG-YR6-COPY1's next part, or
// reused as approved); a refusal is printed as the backend serves it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Card, CardBody, toast } from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { boardCalendarKeys } from '../../calendar/calendar-queries';
import { displayLongDate } from '../../lib/display';
import {
  decisionBlocks,
  extractCommitteeSuggestions,
  type CommitteeDecision,
  type SuggestedCommittee,
} from '../committees/committee-confirmation-decode';
import { committeeConfirmationWords as w } from '../committees/committee-confirmation-words';

const GROUP_LABEL = 'font-sans text-s12 font-w600 tracking-givmo text-text-secondary';

function outcomeOf(decision: CommitteeDecision): string | null {
  if (decision.kind === 'written_consent') return null;
  return decision.votesFor !== null && decision.votesAgainst !== null
    ? w.carriedTally(decision.votesFor, decision.votesAgainst)
    : w.carried;
}

function PracticeLine({ committee }: { committee: SuggestedCommittee }) {
  return (
    <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
      {w.suggestionLine(committee.citation)}
    </p>
  );
}

function Decisions({ committee }: { committee: SuggestedCommittee }) {
  return (
    <>
      {decisionBlocks(committee.decisions).map((block) => {
        const day = displayLongDate(block.day) ?? '';
        return (
          <div key={block.key} className="flex flex-col gap-6">
            <span className={GROUP_LABEL}>
              {block.kind === 'consent' ? w.byConsent(day) : w.fromMeeting(day)}
            </span>
            <ul className="flex flex-col">
              {block.decisions.map((decision) => {
                const outcome = outcomeOf(decision);
                return (
                  <li
                    key={decision.id}
                    className="flex flex-wrap items-baseline justify-between gap-x-16 gap-y-2 border-t border-border-subtle py-8 first:border-t-0 max-sm:flex-col max-sm:items-start"
                    data-testid={`committee-suggestion-decision-${decision.id}`}
                  >
                    <span className="min-w-0 flex-1 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-strong">
                      {decision.words}
                    </span>
                    {outcome !== null ? (
                      <span className="shrink-0 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary tabular-nums">
                        {outcome}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </>
  );
}

export function CommitteeSuggestionCard({
  orgRef,
  meetingId,
}: {
  orgRef: string;
  meetingId: string;
}) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const suggestionsOptions = operations.officerGetBoardCommitteeSuggestionsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const query = useQuery({ ...suggestionsOptions, select: extractCommitteeSuggestions });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: suggestionsOptions.queryKey }),
      queryClient.invalidateQueries({
        queryKey: operations.officerListBoardAgendaItemsOptions({
          path: { org_ref: orgRef, meeting_id: meetingId },
        }).queryKey,
      }),
      // The press and the dismissal each add a line to the meeting's own history.
      queryClient.invalidateQueries({ queryKey: boardCalendarKeys(orgRef).events(meetingId) }),
    ]);

  const act = useMutation({
    mutationFn: async (kind: 'add' | 'dismiss' | 'restore') => {
      const options = {
        path: { org_ref: orgRef, meeting_id: meetingId },
        throwOnError: true as const,
      };
      if (kind === 'add') await operations.officerAddBoardCommitteeSuggestionItems(options);
      else if (kind === 'dismiss') await operations.officerDismissBoardCommitteeSuggestion(options);
      else await operations.officerRestoreBoardCommitteeSuggestion(options);
    },
    onSuccess: (_data, kind) => {
      if (kind === 'add') toast.success(w.added);
      void refresh();
    },
    onError: (caught) => {
      toast.error(isGivmoError(caught) ? caught.message : w.failed);
      // Put away or added in another tab meanwhile: the card says so once it reads again.
      void refresh();
    },
  });

  const read = query.data ?? null;
  if (read === null || !read.offered || read.committees.length === 0) return null;

  // Put away for this meeting: one quiet line brings it back.
  if (read.dismissedAt !== null) {
    return (
      <Button
        appearance="saas"
        tone="link"
        size="sm"
        className="self-start"
        loading={act.isPending}
        onClick={() => act.mutate('restore')}
        testId="committee-suggestion-restore"
      >
        {w.showAgain}
      </Button>
    );
  }

  const several = read.committees.length > 1;
  const first = read.committees[0]!;
  return (
    <Card testId="committee-suggestion-card">
      <CardBody className="flex flex-col gap-12">
        {/* One committee: its name is the card's title and its practice sits under it (frame c7,
            CBG-FIX97). Several: one title, and each committee's name and practice over its own
            decisions. */}
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {several ? w.severalTitle : first.name}
          </h2>
          {several ? null : <PracticeLine committee={first} />}
        </div>
        {read.committees.map((committee) => (
          <div
            key={committee.committeeId}
            className="flex flex-col gap-10"
            data-testid={`committee-suggestion-${committee.committeeId}`}
          >
            {several ? (
              <div className="flex flex-col gap-4 border-t border-border-subtle pt-12">
                <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                  {committee.name}
                </span>
                <PracticeLine committee={committee} />
              </div>
            ) : null}
            <Decisions committee={committee} />
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-10 pt-4">
          <Button
            appearance="saas"
            tone="primary"
            loading={act.isPending && act.variables === 'add'}
            disabled={act.isPending}
            onClick={() => act.mutate('add')}
            testId="committee-suggestion-add"
          >
            {w.addToAgenda}
          </Button>
          <Button
            appearance="saas"
            tone="ghost"
            disabled={act.isPending}
            onClick={() => act.mutate('dismiss')}
            testId="committee-suggestion-dismiss"
          >
            {w.notForThisMeeting}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
